import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { getActiveTemplate } from "../../settings/api/displayTemplates";
import {
  ANALYTICS_CARDS,
  ASSET_ANALYTICS_CARDS,
  DASHBOARD_CARDS,
  resolveCardConfig,
} from "../constants/cardRegistry";
import { getCardStyleCSSVars } from "../constants/cardStylePresets";
import { COLORWAY_TO_SETTINGS_MAP } from "../constants/colorwayPresets";

const SettingsContext = createContext();

// Default values for all settings
const DEFAULTS = {
  showSalesOverTime: true,
  showRentalsOverTime: true,
  showImpressionsOverTime: true,
  showImpressionRevenueOverTime: true,
  showTotalImpressionsCard: true,
  showTotalSalesCard: true,
  showTotalRevenueCard: true,
  showTotalEarningsOverTime: true,
  showROIOverTime: true,
  showSalesDetails: true,
  showDetailedBreakdown: true,
  showAnimatedNumbers: true,
  animatedNumberSections: {
    dashboardCards: true,
    chartTotals: true,
    statsCards: true,
    tables: true,
    sourceDetails: true,
    admin: true,
  },
  impressionsGraphColor: "#009efd",
  salesGraphColor: "#009efd",
  revenueGraphColor: "#009efd",
  salesOverTimeGraphColor: "#009efd",
  rentalsOverTimeGraphColor: "#009efd",
  impressionsOverTimeGraphColor: "#009efd",
  impressionRevenueOverTimeGraphColor: "#009efd",
  totalEarningsOverTimeGraphColor: "#009efd",
  roiGraphColor: "#28a745",
  roiOverTimeGraphColor: "#28a745",
  customPrimaryColor: null,
  cardStyle: "clean",
};

// Keys that store booleans (read as string from localStorage)
const BOOLEAN_KEYS = new Set([
  "showSalesOverTime",
  "showRentalsOverTime",
  "showImpressionsOverTime",
  "showImpressionRevenueOverTime",
  "showTotalImpressionsCard",
  "showTotalSalesCard",
  "showTotalRevenueCard",
  "showTotalEarningsOverTime",
  "showROIOverTime",
  "showSalesDetails",
  "showDetailedBreakdown",
  "showAnimatedNumbers",
]);

// Keys that store JSON objects
const JSON_KEYS = new Set(["animatedNumberSections"]);

function loadInitialState() {
  const state = {};
  for (const [key, defaultValue] of Object.entries(DEFAULTS)) {
    const saved = localStorage.getItem(key);
    if (saved === null) {
      state[key] = defaultValue;
    } else if (BOOLEAN_KEYS.has(key)) {
      state[key] = saved === "true";
    } else if (JSON_KEYS.has(key)) {
      try {
        state[key] = JSON.parse(saved);
      } catch {
        state[key] = defaultValue;
      }
    } else {
      state[key] = saved;
    }
  }
  return state;
}

function settingsReducer(state, action) {
  if (action.type === "SET") {
    return { ...state, [action.key]: action.value };
  }
  return state;
}

/**
 * Resolve a date range config into { period_start, period_end } strings.
 * Supports "offset" mode (months_back + window_months) and "explicit" mode.
 * Falls back to last 4 months if no config is provided.
 */
export function resolveDateRange(dateRangeConfig) {
  if (!dateRangeConfig) {
    // Fallback: no date filter (show all available data)
    return {
      period_start: null,
      period_end: null,
    };
  }

  if (dateRangeConfig.mode === "explicit") {
    return {
      period_start: dateRangeConfig.period_start,
      period_end: dateRangeConfig.period_end,
    };
  }

  // Offset mode: go back months_back, then show a window of window_months
  const monthsBack = dateRangeConfig.months_back ?? 3;
  const windowMonths = dateRangeConfig.window_months ?? 1;

  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
  const end = new Date(
    start.getFullYear(),
    start.getMonth() + windowMonths,
    0, // day 0 = last day of previous month
  );

  return {
    period_start: start.toISOString().split("T")[0],
    period_end: end.toISOString().split("T")[0],
  };
}

/**
 * Apply local chart type overrides on top of resolved card configs.
 * Keys are formatted as "configKey:cardKey" → chart_type value.
 */
function applyLocalOverrides(resolvedConfig, configKey, overrides) {
  if (!overrides || Object.keys(overrides).length === 0) return resolvedConfig;
  return resolvedConfig.map((card) => {
    const overrideKey = `${configKey}:${card.key}`;
    if (overrides[overrideKey]) {
      return { ...card, chart_type: overrides[overrideKey] };
    }
    return card;
  });
}

export const SettingsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(settingsReducer, null, loadInitialState);
  const [displayTemplate, setDisplayTemplate] = useState(null);
  const [displayTemplateLoading, setDisplayTemplateLoading] = useState(true);
  const [wasReset, setWasReset] = useState(false);
  const [previewOverrides, setPreviewOverrides] = useState(null);
  const [localCardOverrides, setLocalCardOverrides] = useState({});

  // Fetch the active display template on mount
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const result = await getActiveTemplate();
        setDisplayTemplate(result.template);
        if (result.was_reset) {
          setWasReset(true);
        }
      } catch {
        // No template or not authenticated yet — use defaults
        setDisplayTemplate(null);
      } finally {
        setDisplayTemplateLoading(false);
      }
    };
    fetchTemplate();
  }, []);

  // Apply colorway from template when it loads (only if template has one)
  useEffect(() => {
    if (!displayTemplate?.colorway_config) return;
    const colorway = displayTemplate.colorway_config;
    for (const [templateKey, settingsKey] of Object.entries(
      COLORWAY_TO_SETTINGS_MAP,
    )) {
      if (colorway[templateKey]) {
        dispatch({
          type: "SET",
          key: settingsKey,
          value: colorway[templateKey],
        });
      }
    }
  }, [displayTemplate]);

  // Single useEffect to sync all changes to localStorage
  useEffect(() => {
    for (const [key, value] of Object.entries(state)) {
      if (value === null) {
        localStorage.removeItem(key);
      } else if (JSON_KEYS.has(key)) {
        localStorage.setItem(key, JSON.stringify(value));
      } else {
        localStorage.setItem(key, value.toString());
      }
    }

    // Apply custom primary color to CSS
    if (state.customPrimaryColor) {
      document.documentElement.style.setProperty(
        "--primary-color",
        state.customPrimaryColor,
      );
    } else {
      document.documentElement.style.removeProperty("--primary-color");
    }

    // Apply card style CSS custom properties
    const mode = document.documentElement.classList.contains("dark")
      ? "dark"
      : document.body.getAttribute("data-theme") === "dark"
        ? "dark"
        : "light";
    const cssVars = getCardStyleCSSVars(state.cardStyle, mode);
    for (const [prop, value] of Object.entries(cssVars)) {
      document.documentElement.style.setProperty(prop, value);
    }
  }, [state]);

  // Create setter functions that match the existing API
  const makeSetter = useCallback(
    (key) => (value) => {
      dispatch({
        type: "SET",
        key,
        value: typeof value === "function" ? value(state[key]) : value,
      });
    },
    [state],
  );

  const value = {
    // Boolean toggle settings
    showSalesOverTime: state.showSalesOverTime,
    setShowSalesOverTime: makeSetter("showSalesOverTime"),
    showRentalsOverTime: state.showRentalsOverTime,
    setShowRentalsOverTime: makeSetter("showRentalsOverTime"),
    showImpressionsOverTime: state.showImpressionsOverTime,
    setShowImpressionsOverTime: makeSetter("showImpressionsOverTime"),
    showImpressionRevenueOverTime: state.showImpressionRevenueOverTime,
    setShowImpressionRevenueOverTime: makeSetter(
      "showImpressionRevenueOverTime",
    ),
    showTotalImpressionsCard: state.showTotalImpressionsCard,
    setShowTotalImpressionsCard: makeSetter("showTotalImpressionsCard"),
    showTotalSalesCard: state.showTotalSalesCard,
    setShowTotalSalesCard: makeSetter("showTotalSalesCard"),
    showTotalRevenueCard: state.showTotalRevenueCard,
    setShowTotalRevenueCard: makeSetter("showTotalRevenueCard"),
    showTotalEarningsOverTime: state.showTotalEarningsOverTime,
    setShowTotalEarningsOverTime: makeSetter("showTotalEarningsOverTime"),
    showROIOverTime: state.showROIOverTime,
    setShowROIOverTime: makeSetter("showROIOverTime"),
    showSalesDetails: state.showSalesDetails,
    setShowSalesDetails: makeSetter("showSalesDetails"),
    showDetailedBreakdown: state.showDetailedBreakdown,
    setShowDetailedBreakdown: makeSetter("showDetailedBreakdown"),
    showAnimatedNumbers: state.showAnimatedNumbers,
    setShowAnimatedNumbers: makeSetter("showAnimatedNumbers"),
    animatedNumberSections: state.animatedNumberSections,
    setAnimatedNumberSections: makeSetter("animatedNumberSections"),

    // Graph color settings
    impressionsGraphColor: state.impressionsGraphColor,
    setimpressionsGraphColor: makeSetter("impressionsGraphColor"),
    salesGraphColor: state.salesGraphColor,
    setsalesGraphColor: makeSetter("salesGraphColor"),
    revenueGraphColor: state.revenueGraphColor,
    setrevenueGraphColor: makeSetter("revenueGraphColor"),
    salesOverTimeGraphColor: state.salesOverTimeGraphColor,
    setSalesOverTimeGraphColor: makeSetter("salesOverTimeGraphColor"),
    rentalsOverTimeGraphColor: state.rentalsOverTimeGraphColor,
    setRentalsOverTimeGraphColor: makeSetter("rentalsOverTimeGraphColor"),
    impressionsOverTimeGraphColor: state.impressionsOverTimeGraphColor,
    setImpressionsOverTimeGraphColor: makeSetter(
      "impressionsOverTimeGraphColor",
    ),
    impressionRevenueOverTimeGraphColor:
      state.impressionRevenueOverTimeGraphColor,
    setImpressionRevenueOverTimeGraphColor: makeSetter(
      "impressionRevenueOverTimeGraphColor",
    ),
    totalEarningsOverTimeGraphColor: state.totalEarningsOverTimeGraphColor,
    setTotalEarningsOverTimeGraphColor: makeSetter(
      "totalEarningsOverTimeGraphColor",
    ),
    roiGraphColor: state.roiGraphColor,
    setRoiGraphColor: makeSetter("roiGraphColor"),
    roiOverTimeGraphColor: state.roiOverTimeGraphColor,
    setRoiOverTimeGraphColor: makeSetter("roiOverTimeGraphColor"),
    customPrimaryColor: state.customPrimaryColor,
    setCustomPrimaryColor: makeSetter("customPrimaryColor"),
    cardStyle: state.cardStyle,
    setCardStyle: makeSetter("cardStyle"),

    // Display template
    displayTemplate,
    displayTemplateLoading,
    setDisplayTemplate,
    wasReset,
    setWasReset,
    defaultDateRange: resolveDateRange(displayTemplate?.date_range_config),

    // Preview override mechanism
    setPreviewOverrides,
    clearPreviewOverrides: () => setPreviewOverrides(null),

    // Update a single card's chart type instantly (no page reload)
    updateCardChartType: (configKey, cardKey, newChartType) => {
      setLocalCardOverrides((prev) => ({
        ...prev,
        [`${configKey}:${cardKey}`]: newChartType,
      }));
    },

    // Resolved card configs — preview overrides > local overrides > template
    dashboardCardConfig: applyLocalOverrides(
      resolveCardConfig(
        previewOverrides?.dashboard_config ?? displayTemplate?.dashboard_config,
        DASHBOARD_CARDS,
      ),
      "dashboard_config",
      localCardOverrides,
    ),
    analyticsCardConfig: applyLocalOverrides(
      resolveCardConfig(
        previewOverrides?.analytics_config ?? displayTemplate?.analytics_config,
        ANALYTICS_CARDS,
      ),
      "analytics_config",
      localCardOverrides,
    ),
    assetAnalyticsCardConfig: applyLocalOverrides(
      resolveCardConfig(
        previewOverrides?.asset_analytics_config ??
          displayTemplate?.asset_analytics_config,
        ASSET_ANALYTICS_CARDS,
      ),
      "asset_analytics_config",
      localCardOverrides,
    ),
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
